import { useCallback, useMemo, useState } from 'react';
import { SECTION_ORDER, formatDuration, type PhotoSection } from '@room-verify/shared';
import { api, type Report } from '../api';
import { ar, arDate, arTime, arTimeSeconds } from '../ar';
import { AuditTimeline } from '../components/audit';
import { EvidencePhoto, EvidenceSection } from '../components/evidence';
import {
  IconAlert,
  IconArrowRight,
  IconBath,
  IconBed,
  IconCamera,
  IconCheckCircle,
  IconClock,
  IconKitchen,
  IconPencil,
  IconPrint,
  IconRooms,
  IconShield,
  IconUser,
} from '../components/icons';
import { Lightbox, type LightboxPhoto } from '../components/overlays';
import { Button, Chip, Notice, Skeleton } from '../components/primitives';
import { useAsync } from '../hooks';

const SECTION_LABEL: Record<PhotoSection, string> = {
  ROOM_NUMBER: ar.sectionRoomNumber,
  BATHROOM: ar.sectionBathroom,
  KITCHEN: ar.sectionKitchen,
  ROOM: ar.sectionRoom,
};

const SECTION_ICON: Record<PhotoSection, (props: { size?: number }) => JSX.Element> = {
  ROOM_NUMBER: IconRooms,
  BATHROOM: IconBath,
  KITCHEN: IconKitchen,
  ROOM: IconBed,
};

/**
 * The report: an Apple product-detail page crossed with an evidence document.
 *
 * Hierarchy is carried by type, not by boxes. The room number is the largest thing on
 * the page and is the one place the editorial serif appears on screen — the whole 5%
 * budget, spent where it means something.
 */
export function ReportScreen({
  token,
  inspectionId,
  onBack,
  onEdit,
  onAuthExpired,
  reloadKey,
}: {
  token: string;
  inspectionId: string;
  onBack: () => void;
  onEdit: (report: Report) => void;
  onAuthExpired: () => void;
  reloadKey: number;
}) {
  const report = useAsync(
    () => api.report(token, inspectionId),
    [token, inspectionId, reloadKey],
    onAuthExpired,
  );

  const [photoUrls, setPhotoUrls] = useState<Record<string, string | null>>({});
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const onPhotoReady = useCallback((photoId: string, url: string | null) => {
    setPhotoUrls((previous) => (previous[photoId] === url ? previous : { ...previous, [photoId]: url }));
  }, []);

  const data = report.data;

  const grouped = useMemo(() => {
    if (!data) return [];
    return SECTION_ORDER.map((section) => ({
      section,
      photos: data.photos.filter((photo) => photo.section === section),
    })).filter((group) => group.photos.length > 0);
  }, [data]);

  /** Flat, in capture order — the lightbox walks the whole report, not one section. */
  const lightboxPhotos = useMemo<LightboxPhoto[]>(
    () =>
      grouped.flatMap((group) =>
        group.photos.map((photo) => ({
          id: photo.id,
          section: photo.section,
          sectionLabel: SECTION_LABEL[photo.section],
          sequence: photo.sequence,
          capturedAt: photo.capturedAt,
          url: photoUrls[photo.id] ?? null,
        })),
      ),
    [grouped, photoUrls],
  );

  const indexOf = (photoId: string) => lightboxPhotos.findIndex((photo) => photo.id === photoId);

  const totalPhotos = data?.photos.length ?? 0;
  const readyCount = Object.values(photoUrls).filter(Boolean).length;
  const allReady = totalPhotos > 0 && readyCount >= totalPhotos;

  /**
   * Print is gated on every image having decoded. A print dialog snapshots the page as
   * it is; firing it early produces a PDF with blank evidence panels, which is worse
   * than a short wait — the document exists to carry those images.
   */
  const print = () => {
    if (!allReady) return;
    window.print();
  };

  if (report.loading && !data) {
    return (
      <div className="page page-report report-page">
        <div className="report-skeleton">
          <Skeleton height={140} radius={20} />
          <Skeleton height={96} radius={16} />
          <Skeleton height={280} radius={16} />
        </div>
      </div>
    );
  }

  if (report.error || !data) {
    return (
      <div className="page page-report report-page">
        <Notice tone="critical" role="alert">
          {report.error ?? ar.errNotFound}
        </Notice>
        <Button variant="outline" onClick={onBack} icon={<IconArrowRight size={15} className="flip-rtl" />}>
          {ar.backToList}
        </Button>
      </div>
    );
  }

  const { inspection, photos, edits } = data;
  const lastEdit = edits.at(-1);
  const missingCount = inspection.missingItems.length + (inspection.otherMissingItem ? 1 : 0);

  return (
    <div className="page page-report report-page">
      {/* Screen-only toolbar. Print gets the document alone. */}
      <div className="report-toolbar no-print">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          icon={<IconArrowRight size={15} className="flip-rtl" />}
        >
          {ar.backToList}
        </Button>

        <div className="report-toolbar-end">
          <span className="print-readiness num" aria-live="polite">
            {allReady ? ar.imagesReady(readyCount, totalPhotos) : ar.preparingImages}
          </span>
          <Button variant="ghost" size="sm" onClick={() => onEdit(data)} icon={<IconPencil size={15} />}>
            {ar.edit}
          </Button>
          {/* One primary action only. */}
          <Button
            variant="primary"
            size="sm"
            onClick={print}
            disabled={!allReady}
            icon={<IconPrint size={15} />}
          >
            {ar.print}
          </Button>
        </div>
      </div>

      <article className="report-doc">
        {/* ---------------------------------------------------- masthead */}
        <header className="report-masthead">
          <div className="masthead-brand">
            <span className="masthead-mark" aria-hidden="true">
              <IconShield size={16} />
            </span>
            <span className="masthead-eyebrow">{ar.reportEyebrow}</span>
            <span className="masthead-sep" aria-hidden="true" />
            <span className="masthead-kicker">{ar.reportSubtitle}</span>
          </div>

          <div className="masthead-room">
            <span className="masthead-room-label">{ar.room}</span>
            <span className="masthead-room-number editorial num" dir="ltr">
              {inspection.roomNumber}
            </span>
          </div>

          <div className="masthead-summary">
            <span className="masthead-type">
              {inspection.inspectionType === 'CHECKOUT' ? ar.checkout : ar.clean}
            </span>
            <span className="masthead-verified">
              <IconCheckCircle size={14} />
              {ar.verified}
            </span>
          </div>
        </header>

        {/* ------------------------------------------------- key metadata */}
        <div className="report-facts">
          <div className="fact">
            <span className="fact-label">
              <IconUser size={12} />
              {ar.supervisor}
            </span>
            <span className="fact-value">{inspection.supervisorName}</span>
          </div>
          <div className="fact">
            <span className="fact-label">
              <IconClock size={12} />
              {ar.completionTime}
            </span>
            <span className="fact-value num">{arTime(inspection.completedAt)}</span>
          </div>
          <div className="fact">
            <span className="fact-label">{ar.duration}</span>
            <span className="fact-value num">{formatDuration(inspection.durationSeconds)}</span>
          </div>
          <div className="fact">
            <span className="fact-label">
              <IconCamera size={12} />
              {ar.photos}
            </span>
            <span className="fact-value num">{photos.length}</span>
          </div>
        </div>

        {/* quiet secondary metadata */}
        <dl className="report-meta">
          <div className="meta-pair">
            <dt>{ar.reportDate}</dt>
            <dd>{arDate(inspection.completedAt)}</dd>
          </div>
          <div className="meta-pair">
            <dt>{ar.startTime}</dt>
            <dd className="num">{arTimeSeconds(inspection.startedAt)}</dd>
          </div>
          <div className="meta-pair">
            <dt>{ar.completionTime}</dt>
            <dd className="num">{arTimeSeconds(inspection.completedAt)}</dd>
          </div>
          <div className="meta-pair meta-pair-wide">
            <dt>{ar.inspectionId}</dt>
            <dd className="mono" dir="ltr">
              {inspection.id}
            </dd>
          </div>
        </dl>

        {/* --------------------------------------------- administrative edits */}
        {inspection.editCount > 0 && lastEdit && (
          <section className="edit-notice" aria-label={ar.editedNotice}>
            <span className="edit-notice-mark" aria-hidden="true">
              <IconPencil size={15} />
            </span>
            <div className="edit-notice-body">
              <p className="edit-notice-title">{ar.editedNotice}</p>
              <p className="edit-notice-meta">
                <span>
                  {ar.editedBy}: <strong>{lastEdit.editorName ?? '—'}</strong>
                </span>
                <span className="meta-dot" aria-hidden="true" />
                <span className="num">
                  {ar.editedAt}: <strong>{arTime(lastEdit.occurredAt)}</strong>
                </span>
              </p>
              <button
                className="edit-notice-toggle no-print"
                onClick={() => setHistoryOpen((open) => !open)}
                aria-expanded={historyOpen}
              >
                {historyOpen ? ar.hideHistory : ar.showHistory}
              </button>
            </div>
          </section>
        )}

        {/* In print the history is always shown: a report that hides its own
            corrections is not evidence. */}
        {inspection.editCount > 0 && (
          <section
            className={`report-history${historyOpen ? '' : ' screen-collapsed'}`}
            aria-label={ar.editHistory}
          >
            <h2 className="report-heading">{ar.editHistory}</h2>
            <AuditTimeline edits={edits} />
          </section>
        )}

        {/* ------------------------------------------------------ room items */}
        <section className="report-items" aria-label={ar.roomItems}>
          {inspection.itemsComplete ? (
            // Complete does not deserve a card — a single line is the whole message.
            <p className="items-complete">
              <IconCheckCircle size={16} />
              {ar.itemsComplete}
            </p>
          ) : (
            <div className="items-missing">
              <div className="items-missing-head">
                <IconAlert size={16} />
                <span className="items-missing-title">{ar.missingItemsTitle}</span>
                <Chip tone="warning">
                  <span className="num">{missingCount}</span>
                </Chip>
              </div>
              <ul className="items-missing-list">
                {inspection.missingItems.map((item) => (
                  <li key={item} lang="en" dir="ltr">
                    {item}
                  </li>
                ))}
                {inspection.otherMissingItem && (
                  <li>
                    {ar.otherItem}: {inspection.otherMissingItem}
                  </li>
                )}
              </ul>
            </div>
          )}
        </section>

        {/* --------------------------------------------------------- evidence */}
        <div className="report-evidence">
          <h2 className="report-heading report-heading-lead">{ar.evidenceHeading}</h2>

          {grouped.map((group) => {
            const Icon = SECTION_ICON[group.section];
            return (
              <EvidenceSection
                key={group.section}
                title={SECTION_LABEL[group.section]}
                count={group.photos.length}
                icon={<Icon size={14} />}
              >
                {group.photos.map((photo) => (
                  <EvidencePhoto
                    key={photo.id}
                    token={token}
                    photoId={photo.id}
                    alt={`${SECTION_LABEL[photo.section]} ${photo.sequence}`}
                    sequence={photo.sequence}
                    timeLabel={arTime(photo.capturedAt)}
                    clockAdjusted={photo.clockAdjusted}
                    onReady={onPhotoReady}
                    onOpen={() => setLightboxIndex(indexOf(photo.id))}
                  />
                ))}
              </EvidenceSection>
            );
          })}
        </div>

        <footer className="report-foot">
          <span className="report-foot-brand">{ar.reportEyebrow}</span>
          <span className="report-foot-id mono" dir="ltr">
            {inspection.id}
          </span>
        </footer>
      </article>

      {/* Sticky action bar on phones, where the toolbar has scrolled away. */}
      <div className="report-actionbar no-print">
        <Button variant="ghost" size="sm" onClick={() => onEdit(data)} icon={<IconPencil size={15} />}>
          {ar.edit}
        </Button>
        <Button
          variant="primary"
          onClick={print}
          disabled={!allReady}
          icon={<IconPrint size={15} />}
          block
        >
          {ar.print}
        </Button>
      </div>

      <Lightbox
        photos={lightboxPhotos}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNavigate={setLightboxIndex}
        formatTime={arTime}
        labels={{
          close: ar.lightboxClose,
          previous: ar.lightboxPrev,
          next: ar.lightboxNext,
          captured: ar.capturedAt,
          of: ar.lightboxOf,
        }}
      />
    </div>
  );
}
